import React, { useEffect } from 'react';
import { Stack, Copy } from '@grubhubprod/gh-cookbook-react';
import { ConfirmationStepCardContainer } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationStepCardContainer/ConfirmationStepCardContainer';
import {
  SignupStep,
  useSignupStatus,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { useFetchBalancePaymentMethods } from 'src/components/selfService/common/Balance/hooks/useFetchBalancePaymentMethods';
import { PaymentMethodItemDisplay } from 'src/components/selfService/common/Balance/components/PaymentMethodItemDisplay/PaymentMethodItemDisplay';
import { ConfirmationSummaryRow } from 'src/components/selfService/features/Signup/steps/ConfirmationStep/ConfirmationSummaryRow/ConfirmationSummaryRow';
import {
  getCreditLimitText,
  getDisplayedPaymentMethods,
  getPaymentMethodDescription,
} from './ValidationSummaryCard.utils';

interface ValidationSummaryCardProps {
  onDataLoaded: () => void;
}

export function ValidationSummaryCard({
  onDataLoaded,
}: ValidationSummaryCardProps): JSX.Element {
  const { balance, getStepNumber } = useSignupStatus();
  const {
    isLoading: isLoadingPaymentMethods,
    hasError: hasPaymentMethodsError,
    balancePaymentMethods,
    fetchBalancePaymentMethods,
  } = useFetchBalancePaymentMethods(balance.externalId);

  const paymentMethods = getDisplayedPaymentMethods(balancePaymentMethods);

  useEffect(() => {
    const isDataLoaded = !isLoadingPaymentMethods && !hasPaymentMethodsError;
    if (isDataLoaded) {
      onDataLoaded();
    }
  }, [isLoadingPaymentMethods, hasPaymentMethodsError, onDataLoaded]);

  return (
    <ConfirmationStepCardContainer
      stepName="Financial information"
      isLoadingData={isLoadingPaymentMethods}
      hasError={hasPaymentMethodsError}
      reloadData={fetchBalancePaymentMethods}
      linkText="Edit"
      stepNumber={getStepNumber(SignupStep.VALIDATION)}
    >
      <ConfirmationSummaryRow label="Status" y="x-sm">
        Approved
      </ConfirmationSummaryRow>

      <ConfirmationSummaryRow label="Credit limit" y="x-sm">
        {getCreditLimitText(balance.creditLimit)}
      </ConfirmationSummaryRow>

      {paymentMethods.length > 0 && (
        <ConfirmationSummaryRow label="Payment" y="none">
          <>
            {paymentMethods.map((method) => (
              <Stack key={method.item.id} y="x-sm">
                <PaymentMethodItemDisplay
                  method={method}
                  showDefaultBadge={false}
                  showBorder={false}
                />
              </Stack>
            ))}
          </>
          <Copy variant="caption" color="secondaryText">
            {getPaymentMethodDescription(balance.creditLimit)}
          </Copy>
        </ConfirmationSummaryRow>
      )}
    </ConfirmationStepCardContainer>
  );
}
